
import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AbmConfig } from "@/types/settings";
import { PatchPolicyFormValues } from "../../schema";
import { mockCustomers } from "@/utils/customerGroups";
import { Textarea } from "@/components/ui/textarea";
import { Eye, EyeOff, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface AbmSettingsFormProps {
  initialData: AbmConfig | null;
  onSubmit: (data: AbmConfig) => void;
  onCancel: () => void;
  form: UseFormReturn<PatchPolicyFormValues>;
}

export function AbmSettingsForm({ initialData, onSubmit, onCancel, form }: AbmSettingsFormProps) {
  const [formData, setFormData] = React.useState<AbmConfig>({
    id: initialData?.id || "",
    name: initialData?.name || "",
    customerId: initialData?.customerId || "global",
    isDefault: initialData?.isDefault || false,
    clientId: initialData?.clientId || "",
    clientSecret: initialData?.clientSecret || "",
    organizationId: initialData?.organizationId || "",
    serverToken: initialData?.serverToken || "",
    description: initialData?.description || "",
    connectionStatus: initialData?.connectionStatus || 'disconnected'
  });
  const [showSecret, setShowSecret] = React.useState(false);
  const [isTesting, setIsTesting] = React.useState(false);

  const handleChange = (field: keyof AbmConfig, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <TooltipProvider>
      <form onSubmit={handleSubmit} className="space-y-4 pt-4">
      <div className="grid gap-4 grid-cols-2">
        <div className="space-y-2">
          <FormLabel>Configuration Name</FormLabel>
          <Input 
            value={formData.name} 
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Configuration Name"
            required
          />
        </div>

        <div className="space-y-2">
          <FormLabel>Assign to Customer</FormLabel>
          <Select 
            value={formData.customerId} 
            onValueChange={(value) => handleChange("customerId", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="global">Global (All Customers)</SelectItem>
              {mockCustomers.map((customer) => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <FormLabel>Description</FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          placeholder="Describe this configuration..."
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <FormLabel>Client ID</FormLabel>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer transition-colors" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Found in Apple Business Manager → Settings → API Integration</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <Input 
          value={formData.clientId}
          onChange={(e) => handleChange("clientId", e.target.value)}
          placeholder="Enter OAuth client ID from ABM"
          required
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <FormLabel>Client Secret</FormLabel>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer transition-colors" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Your OAuth client secret will be encrypted and stored securely</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="relative">
          <Input 
            type={showSecret ? "text" : "password"}
            value={formData.clientSecret}
            onChange={(e) => handleChange("clientSecret", e.target.value)}
            placeholder="Enter OAuth client secret"
            className="pr-10"
            required
          />
          <button
            type="button"
            onClick={() => setShowSecret(!showSecret)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showSecret ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <FormLabel>Organization ID</FormLabel>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer transition-colors" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Your unique Apple Business Manager organization identifier</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <Input 
          value={formData.organizationId}
          onChange={(e) => handleChange("organizationId", e.target.value)}
          placeholder="Enter your ABM organization ID"
          required
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {initialData ? "Update" : "Create"} Configuration
        </Button>
      </div>
      </form>
    </TooltipProvider>
  );
}
